import React, {
  createContext,
  ReactNode,
  useEffect,
  useReducer,
  useContext,
} from 'react';
import useVgsCollect from '../../hooks/useVgsCollect';
import { createShortLivedToken } from '../../services/vault/token';
import formatVariables from './formatVariables';
import logger from '../../util/logger';

export type VgsCollectContextState<ResponseData> = {
  form?: VGSForm<ResponseData>;
};

export type VgsCollectAction<ResponseData> = {
  type: 'SET_FORM';
  value: VGSForm<ResponseData>;
};

export const vgsCollectInitialState: VgsCollectContextState<unknown> = {};

const VgsCollectContext = createContext<{
  state: VgsCollectContextState<unknown>;
  dispatch: React.Dispatch<VgsCollectAction<unknown>>;
  submit: (props: {
    query: string;
    variables: Record<string, unknown>;
  }) => Promise<unknown>;
}>({
  state: vgsCollectInitialState,
  dispatch: () => false,
  submit: () => Promise.resolve(),
});

export default VgsCollectContext;

function vgsCollectReducer<ResponseData>(
  state: VgsCollectContextState<ResponseData>,
  action: VgsCollectAction<ResponseData>,
): VgsCollectContextState<ResponseData> {
  switch (action.type) {
    case 'SET_FORM':
      return { ...state, form: action.value };
    default:
      return state;
  }
}

export function VgsCollectProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(
    vgsCollectReducer,
    vgsCollectInitialState,
  );
  const vgsCollectForm = useVgsCollect();

  useEffect(() => {
    if (!state.form && vgsCollectForm) {
      dispatch({
        type: 'SET_FORM',
        value: vgsCollectForm,
      });
    }
  }, [state.form, vgsCollectForm]);

  const submit = async ({
    query,
    variables,
  }: {
    query: string;
    variables: Record<string, unknown>;
  }) => {
    if (!state.form) {
      return;
    }
    const token = await createShortLivedToken();
    return new Promise((resolve, reject) => {
      state.form?.submit(
        '/graphql',
        {
          data: (formValues: Record<string, unknown>): VgsRequestBody => ({
            variables: formatVariables(variables, formValues),
            query,
          }),
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        },
        async (status, data) => {
          logger(`VGS submit status ${status}`);
          logger('VGS submit data', false, data);
          if (data.errors) {
            reject(data.errors);
          } else if (Array.isArray(data) && data[0]?.errors) {
            reject(data[0].errors);
          } else if (data.data) {
            resolve(data.data);
          } else {
            resolve(data);
          }
        },
        async (errors) => {
          logger('VgsCollect submit error', false, errors);
          reject(errors);
        },
      );
    });
  };

  return (
    <VgsCollectContext.Provider
      value={{
        state,
        dispatch,
        submit,
      }}
    >
      {children}
    </VgsCollectContext.Provider>
  );
}

export const useVgsCollectContext = () => {
  const context = useContext(VgsCollectContext);
  if (!context) {
    throw new Error(
      'useVgsCollectContext must be used within a VgsCollectProvider',
    );
  }
  return context;
};

export const useVaultSubmit = () => {
  const context = useVgsCollectContext();
  return context.submit;
};
